import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingDown, DollarSign, Clock, Layers, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_COLORS = {
  openai: "var(--qn-info, #3b82f6)",
  anthropic: "var(--qn-secondary, #f97316)",
  google: "var(--qn-success, #10b981)",
  local: "var(--qn-foreground-subtle, #6b7280)",
};

const MODEL_TO_PROVIDER = {
  "gpt-5.2": "openai", "gpt-4o": "openai", "gpt-4o-mini": "openai",
  "claude-sonnet": "anthropic", "claude-haiku": "anthropic",
  "gemini-3-pro": "google", "gemini-3-flash": "google",
  "kimi-k2.5": "moonshot", "kimi-k2.5-chat": "moonshot",
  "gemma-4-27b-it": "self-hosted", "gemma-4-e4b-it": "self-hosted",
  "gemma-4-e2b-it": "self-hosted", "gemma-4-31b-it": "self-hosted",
  "local": "local",
};

function getProvider(model) {
  return MODEL_TO_PROVIDER[model] || "openai";
}

export default function CostComparisonVisualizer() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("cost");

  useEffect(() => {
    axios.get(`${API}/optimizations/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const costData = useMemo(() => {
    if (!stats?.recent_optimizations?.length) return [];
    return stats.recent_optimizations.slice(0, 8).map((opt, i) => ({
      name: `#${i + 1}`,
      original: +((opt.original_cost || 0) * 1000).toFixed(2),
      optimized: +((opt.optimized_cost || 0) * 1000).toFixed(2),
      reduction: opt.cost_reduction_percent || 0,
      solver: opt.solver_type || "unknown",
    }));
  }, [stats]);

  const providerData = useMemo(() => {
    if (!stats?.recent_optimizations?.length) return [];
    const counts = {};
    stats.recent_optimizations.forEach(opt => {
      Object.values(opt.task_assignments || {}).forEach(model => {
        const prov = getProvider(model);
        counts[prov] = (counts[prov] || 0) + 1;
      });
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [stats]);

  const timeData = useMemo(() => {
    if (!stats?.recent_optimizations?.length) return [];
    return stats.recent_optimizations.slice(0, 8).map((opt, i) => ({
      name: `#${i + 1}`,
      original: +((opt.original_time || 0)).toFixed(2),
      optimized: +((opt.optimized_time || 0)).toFixed(2),
      reduction: opt.time_reduction_percent || 0,
    }));
  }, [stats]);

  if (loading) {
    return (
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="py-12 text-center text-[var(--qn-foreground-muted)]">Loading cost comparison data...</CardContent>
      </Card>
    );
  }

  if (!stats?.recent_optimizations?.length) {
    return (
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-[var(--qn-primary)]" />
            Cost Comparison Visualizer
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center text-[var(--qn-foreground-muted)]">
          Run some optimizations first to see cost comparison data.
        </CardContent>
      </Card>
    );
  }

  const views = [
    { key: "cost", label: "Cost Breakdown", icon: DollarSign },
    { key: "time", label: "Time Savings", icon: Clock },
    { key: "provider", label: "Provider Mix", icon: Layers },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cost-comparison-visualizer">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-[var(--qn-primary)]" />
                Cost Comparison Visualizer
              </CardTitle>
              <CardDescription className="text-[var(--qn-foreground-muted)]">
                Before vs. after optimization across your recent runs
              </CardDescription>
            </div>
            <div className="flex gap-1.5 p-1 bg-[var(--qn-background)] rounded-lg border border-[var(--qn-border)]">
              {views.map(v => (
                <Button
                  key={v.key}
                  size="sm"
                  variant={activeView === v.key ? "default" : "ghost"}
                  className={activeView === v.key ? "btn-primary text-xs h-7" : "text-xs h-7 text-[var(--qn-foreground-muted)]"}
                  onClick={() => setActiveView(v.key)}
                  data-testid={`ccv-tab-${v.key}`}
                >
                  <v.icon className="w-3.5 h-3.5 mr-1" />
                  {v.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <SummaryCard label="Total Runs" value={stats.total_optimizations} />
            <SummaryCard label="Total Saved" value={`$${stats.total_cost_saved.toFixed(2)}`} accent />
            <SummaryCard label="Avg Cost Reduction" value={`${stats.avg_cost_reduction}%`} />
            <SummaryCard label="Avg Time Reduction" value={`${stats.avg_time_reduction}%`} />
          </div>
        </CardHeader>

        <CardContent>
          {activeView === "cost" && (
            <div data-testid="ccv-cost-chart">
              <p className="text-sm text-[var(--qn-foreground-muted)] mb-3">
                Original vs. Optimized cost per run (millicents)
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={costData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--qn-border)" />
                  <XAxis dataKey="name" tick={{ fill: "var(--qn-foreground-muted)", fontSize: 12 }} />
                  <YAxis tick={{ fill: "var(--qn-foreground-muted)", fontSize: 12 }} />
                  <Tooltip content={<CostTooltip />} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
                  <Bar dataKey="original" name="Original" fill="var(--qn-foreground-subtle)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="optimized" name="Optimized" fill="var(--qn-primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeView === "time" && (
            <div data-testid="ccv-time-chart">
              <p className="text-sm text-[var(--qn-foreground-muted)] mb-3">
                Sequential vs. Parallelized execution time (seconds)
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={timeData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--qn-border)" />
                  <XAxis dataKey="name" tick={{ fill: "var(--qn-foreground-muted)", fontSize: 12 }} />
                  <YAxis tick={{ fill: "var(--qn-foreground-muted)", fontSize: 12 }} />
                  <Tooltip content={<TimeTooltip />} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
                  <Bar dataKey="original" name="Sequential" fill="var(--qn-foreground-subtle)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="optimized" name="Parallel" fill="var(--qn-success, #10b981)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeView === "provider" && (
            <div data-testid="ccv-provider-chart" className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <p className="text-sm text-[var(--qn-foreground-muted)] mb-3">
                  Task routing distribution across providers
                </p>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={providerData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {providerData.map((entry) => (
                        <Cell key={entry.name} fill={PROVIDER_COLORS[entry.name] || "#888"} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, color: "var(--qn-foreground)", fontSize: 12 }} wrapperStyle={{ outline: "none" }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {providerData.map(p => (
                  <div key={p.name} className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)]">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: PROVIDER_COLORS[p.name] || "#888" }} />
                      <span className="text-sm font-medium capitalize">{p.name}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{p.value} tasks</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimization history table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="ccv-history-table">
              <thead>
                <tr className="border-b border-[var(--qn-border)] text-left text-[var(--qn-foreground-muted)]">
                  <th className="py-2 pr-4">Run</th>
                  <th className="py-2 pr-4">Cost</th>
                  <th className="py-2 pr-4">Reduction</th>
                  <th className="py-2 pr-4">Time Saved</th>
                  <th className="py-2 pr-4">Solver</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_optimizations.slice(0, 8).map((opt, i) => (
                  <tr key={opt.optimization_id} className="border-b border-[var(--qn-border)]/50">
                    <td className="py-2.5 pr-4 font-mono text-xs">#{i + 1}</td>
                    <td className="py-2.5 pr-4">
                      <span className="text-[var(--qn-foreground-muted)] line-through mr-1.5">${(opt.original_cost * 1000).toFixed(1)}</span>
                      <ArrowRight className="w-3 h-3 inline mx-0.5 text-[var(--qn-foreground-subtle)]" />
                      <span className="text-[var(--qn-primary)] font-medium ml-1">${(opt.optimized_cost * 1000).toFixed(1)}</span>
                      <span className="text-[var(--qn-foreground-subtle)] text-xs ml-0.5">mc</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge variant="secondary" className="bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] font-mono text-xs">
                        -{opt.cost_reduction_percent}%
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge variant="secondary" className="bg-[var(--qn-success,#10b981)]/10 text-[var(--qn-success,#10b981)] font-mono text-xs">
                        -{opt.time_reduction_percent}%
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge variant="outline" className="text-xs font-mono border-[var(--qn-border)]">
                        {opt.solver_type?.replace("_", " ")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="p-3 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)]">
      <p className="text-xs text-[var(--qn-foreground-muted)]">{label}</p>
      <p className={`text-lg font-semibold font-mono ${accent ? "text-[var(--qn-primary)]" : ""}`}>{value}</p>
    </div>
  );
}

function CostTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-lg p-3 shadow-lg text-sm">
      <p className="font-medium mb-1">Run {d?.name}</p>
      <p><span className="text-[var(--qn-foreground-muted)]">Original:</span> {d?.original} mc</p>
      <p><span className="text-[var(--qn-primary)]">Optimized:</span> {d?.optimized} mc</p>
      <p className="mt-1 font-mono text-[var(--qn-primary)]">-{d?.reduction}% saved</p>
    </div>
  );
}

function TimeTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-lg p-3 shadow-lg text-sm">
      <p className="font-medium mb-1">Run {d?.name}</p>
      <p><span className="text-[var(--qn-foreground-muted)]">Sequential:</span> {d?.original}s</p>
      <p><span className="text-[var(--qn-success,#10b981)]">Parallel:</span> {d?.optimized}s</p>
      <p className="mt-1 font-mono text-[var(--qn-success,#10b981)]">-{d?.reduction}% faster</p>
    </div>
  );
}
